import React, { useEffect } from "react";
import { Helmet } from 'react-helmet';
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { 
  Bus, 
  MapPin, 
  Users, 
  BadgeIndianRupee, 
  Car, 
  Compass, 
  ShieldCheck, 
  ChevronRight, 
  Info, 
  Ticket
} from "lucide-react";
import img1 from "../../Assets/img/parshvanath-temple-khajuraho.jpg";
import qrImg from "../../Assets/certificate/payment.png";

const TransportCharges = () => {
  const { t, i18n } = useTranslation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [i18n.language]);

  const guideCharges = [
    { lang: "ENGLISH", pax: "01 - 04 / 05 - 15", rate: "2500 / 3000" },
    { lang: "SPANISH", pax: "01 - 04 / 05 - 15", rate: "2500 / 3500" },
    { lang: "RUSSIAN", pax: "01 - 04 / 05 - 15", rate: "2500 / 3500" },
    { lang: "FRENCH", pax: "01 - 04 / 05 - 15", rate: "2500 / 3500" },
    { lang: "ITALIAN", pax: "01 - 04 / 05 - 15", rate: "2500 / 3500" },
  ];

  const monumentFees = [
    { name: "Western Group", foreigner: "600.00", indian: "40.00" },
    { name: "Light & Sound Show", foreigner: "700.00", indian: "250.00" },
    { name: "Ajay Garh Fort", foreigner: "100.00", indian: "30.00" },
    { name: "Kalinjar Fort", foreigner: "250.00", indian: "100.00" },
    { name: "Orchha Fort", foreigner: "250.00", indian: "30.00" },
    { name: "Panna Jeep Safari (Per Jeep)", foreigner: "6500.00", indian: "3500.00" },
    { name: "Raneh Waterfall", foreigner: "700.00", indian: "700.00" },
    { name: "Pandey Waterfall", foreigner: "400.00", indian: "400.00" },
  ];

  const transportRates = [
    { dest: "GWALIOR - KHAJURAHO", etios: "9,000.00", crysta: "11,000.00", tempo: "18,000.00" },
    { dest: "JHANSI - KHAJURAHO VIA ORCHHA", etios: "7,000.00", crysta: "8,500.00", tempo: "12,000.00" },
    { dest: "KHAJURAHO - BANDHAVGARH", etios: "7,500.00", crysta: "10,000.00", tempo: "12,000.00" },
    { dest: "JABALPUR - BANDHAVGARH", etios: "6,500.00", crysta: "7,500.00", tempo: "12,500.00" },
    { dest: "BANDHAVGARH - KANHA", etios: "8,000.00", crysta: "8,500.00", tempo: "11,500.00" },
    { dest: "KANHA - PENCH", etios: "6,000.00", crysta: "8,500.00", tempo: "11,500.00" },
    { dest: "PENCH - NAGPUR", etios: "3,500.00", crysta: "5,000.00", tempo: "8,500.00" },
    { dest: "KANHA - NAGPUR", etios: "7,000.00", crysta: "10,500.00", tempo: "18,000.00" },
    { dest: "KHAJURAHO - VARANASI", etios: "11,000.00", crysta: "14,000.00", tempo: "31,000.00" },
    { dest: "KHAJURAHO - AGRA", etios: "11,500.00", crysta: "15,000.00", tempo: "22,500.00" },
    { dest: "KANPUR - KHAJURAHO", etios: "7,500.00", crysta: "9,500.00", tempo: "12,500.00" },
    { dest: "ALLAHABAD - KHAJURAHO", etios: "8,500.00", crysta: "11,500.00", tempo: "18,500.00" },
    { dest: "SATNA - KHAJURAHO", etios: "3,000.00", crysta: "4,500.00", tempo: "7,000.00" },
    { dest: "MAHOBA - KHAJURAHO", etios: "3,000.00", crysta: "4,000.00", tempo: "7,500.00" },
    { dest: "LUCKNOW - KHAJURAHO", etios: "9,500.00", crysta: "14,000.00", tempo: "19,500.00" },
    { dest: "JABALPUR - KHAJURAHO", etios: "7,500.00", crysta: "10,500.00", tempo: "14,000.00" },
  ];

  const localRates = [
    { type: "AIRPORT TO HOTEL", etios: "500.00", innova: "1,500.00" },
    { type: "RAILWAY STATION TO HOTEL", etios: "800.00", innova: "800.00" },
    { type: "HALF DAY CITY TOUR", etios: "1,500.00", innova: "1,500.00" },
    { type: "FULL DAY CITY TOUR", etios: "2,200.00", innova: "2,800.00" },
  ];

  return (
    <main className="bg-[#FCFBFA] text-[#1A202C] font-sans selection:bg-orange-100 selection:text-orange-900">
      <Helmet>
        <title>Transport Charges & Guide Fees | Khajuraho Routes</title>
        <meta name="description" content="View our transparent and competitive transport charges, taxi rates, guide fees, and monument entry fees for Khajuraho and surrounding destinations." />
        <meta name="keywords" content="Khajuraho taxi rates, transport charges Khajuraho, Khajuraho guide fees, monument tickets Khajuraho" />
        <link rel="canonical" href="https://khajurahoroutes.com/transport-charges" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://khajurahoroutes.com/transport-charges" />
        <meta property="og:title" content="Transport Charges & Guide Fees | Khajuraho Routes" />
        <meta property="og:description" content="View our transparent and competitive transport charges, taxi rates, guide fees, and monument entry fees for Khajuraho and surrounding destinations." />
        <meta property="og:image" content="https://khajurahoroutes.com/android-chrome-512x512.png" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Transport Charges & Guide Fees | Khajuraho Routes" />
        <meta name="twitter:description" content="View our transparent and competitive transport charges, taxi rates, guide fees, and monument entry fees for Khajuraho and surrounding destinations." />
        <meta name="twitter:image" content="https://khajurahoroutes.com/android-chrome-512x512.png" />

        {/* JSON-LD Schema */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Service",
              "name": "Khajuraho Transport & Guide Tariff",
              "provider": {
                "@type": "TravelAgency",
                "name": "Khajuraho Routes"
              },
              "areaServed": {
                "@type": "City",
                "name": "Khajuraho"
              },
              "description": "Official transport charges, taxi rates, and guide fees for Khajuraho."
            }
          `}
        </script>
      </Helmet>
      {/* --- HERO SECTION --- */}
      <section className="relative h-[60vh] flex items-center overflow-hidden bg-[#0A0A0A]">
        <div className="absolute inset-0 z-0">
          <img src={img1} className="w-full h-full object-cover opacity-50 scale-105" alt="Khajuraho transport charges - taxi and bus rental price" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="h-[2px] w-12 bg-orange-main" />
              <span className="text-orange-main font-black tracking-[0.4em] text-[10px] uppercase">{t('transport_tariff_badge')}</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-black text-white leading-tight uppercase tracking-tight">
              {t('transport_heading')}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* --- CONTENT SECTION --- */}
      <section className="py-12 bg-[#FDFCFB]">
        <div className="max-w-7xl mx-auto px-2 lg:px-12">
          
          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            {/* Guide Charges */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-main/10 rounded-xl flex items-center justify-center text-orange-main">
                  <Users size={20} />
                </div>
                <h2 className="text-lg font-black uppercase tracking-tight text-gray-900">{t('transport_guide_charges')}</h2>
              </div>

              <div className="overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">
                <table className="w-full border-collapse table-fixed">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border border-gray-300 px-3 py-4 text-[9px] lg:text-[11px] uppercase tracking-wider font-black text-left text-gray-600 w-[40%]">{t('transport_language')}</th>
                      <th className="border border-gray-300 px-3 py-4 text-[9px] lg:text-[11px] uppercase tracking-wider font-black text-center text-gray-600 w-[30%]">{t('transport_pax')}</th>
                      <th className="border border-gray-300 px-3 py-4 text-[9px] lg:text-[11px] uppercase tracking-wider font-black text-right text-gray-600 w-[30%]">{t('transport_rate')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {guideCharges.map((item, i) => (
                      <tr key={i} className="hover:bg-orange-50/30 transition-colors">
                        <td className="border border-gray-300 px-3 py-4 text-[10px] lg:text-[13px] font-bold text-gray-800">{t(item.lang)}</td>
                        <td className="border border-gray-300 px-3 py-4 text-[9px] lg:text-[12px] font-medium text-gray-500 text-center">{item.pax}</td>
                        <td className="border border-gray-300 px-3 py-4 text-[10px] lg:text-[13px] font-black text-orange-main text-right">{item.rate}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>

            {/* Monument Fees */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-main/10 rounded-xl flex items-center justify-center text-orange-main">
                  <Ticket size={20} />
                </div>
                <h2 className="text-lg font-black uppercase tracking-tight text-gray-900">{t('transport_entrance_fees')}</h2>
              </div>

              <div className="overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">
                <table className="w-full border-collapse table-fixed">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border border-gray-300 px-3 py-4 text-[9px] lg:text-[11px] uppercase tracking-wider font-black text-left text-gray-600 w-[40%]">{t('transport_monument')}</th>
                      <th className="border border-gray-300 px-3 py-4 text-[9px] lg:text-[11px] uppercase tracking-wider font-black text-center text-gray-600 w-[30%]">{t('transport_foreigner')}</th>
                      <th className="border border-gray-300 px-3 py-4 text-[9px] lg:text-[11px] uppercase tracking-wider font-black text-right text-gray-600 w-[30%]">{t('transport_indian')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monumentFees.map((item, i) => (
                      <tr key={i} className="hover:bg-orange-50/30 transition-colors">
                        <td className="border border-gray-300 px-3 py-4 text-[10px] lg:text-[13px] font-bold text-gray-800 break-words">{t(item.name)}</td>
                        <td className="border border-gray-300 px-3 py-4 text-[9px] lg:text-[12px] font-medium text-gray-500 text-center">₹ {item.foreigner}</td>
                        <td className="border border-gray-300 px-3 py-4 text-[9px] lg:text-[12px] font-medium text-gray-500 text-right">₹ {item.indian}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </div>

          {/* Transport Rates */}
          {/* <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-orange-main/10 rounded-2xl flex items-center justify-center text-orange-main">
                <Car size={24} />
              </div>
              <h2 className="text-xl font-black uppercase tracking-tight text-gray-900">Inter-City Transfers</h2>
            </div>

            <div className="overflow-hidden rounded-2xl border border-gray-300 bg-white shadow-lg">
              <table className="w-full border-collapse table-fixed">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border border-gray-300 px-2 lg:px-8 py-5 text-[8px] lg:text-[11px] uppercase tracking-tighter font-black text-left text-gray-600 w-[38%]">Route</th>
                    <th className="border border-gray-300 px-1 lg:px-6 py-5 text-[8px] lg:text-[11px] uppercase tracking-tighter font-black text-center text-gray-600">Etios</th>
                    <th className="border border-gray-300 px-1 lg:px-6 py-5 text-[8px] lg:text-[11px] uppercase tracking-tighter font-black text-center text-gray-600">Crysta</th>
                    <th className="border border-gray-300 px-2 lg:px-8 py-5 text-[8px] lg:text-[11px] uppercase tracking-tighter font-black text-right text-gray-600 w-[18%]">Tempo</th>
                  </tr>
                </thead>
                <tbody>
                  {transportRates.map((item, i) => (
                    <tr key={i} className={`${i % 2 === 0 ? "bg-white" : "bg-gray-50/50"} hover:bg-orange-50/50 transition-all`}>
                      <td className="border border-gray-300 px-2 lg:px-8 py-4 text-[9px] lg:text-[14px] font-bold text-gray-800 break-words">{t(item.dest)}</td>
                      <td className="border border-gray-300 px-1 lg:px-6 py-4 text-[8px] lg:text-[13px] font-medium text-gray-500 text-center">₹ {item.etios}</td>
                      <td className="border border-gray-300 px-1 lg:px-6 py-4 text-[8px] lg:text-[13px] font-medium text-gray-500 text-center">₹ {item.crysta}</td>
                      <td className="border border-gray-300 px-2 lg:px-8 py-4 text-[9px] lg:text-[14px] font-bold text-orange-main text-right">₹ {item.tempo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

         
            <div className="mt-8 p-6 bg-gray-900 rounded-3xl text-white relative overflow-hidden shadow-xl">
              <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
                <div className="space-y-2 text-center md:text-left">
                  <h3 className="text-lg font-black uppercase tracking-tight text-orange-main">Terms & Inclusions</h3>
                  <p className="text-gray-400 text-[10px] md:text-xs font-bold leading-relaxed">
                    GST 5% extra. Includes Toll Tax, Parking, Fuel & Driver Allowance.
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <ShieldCheck size={24} className="text-orange-main" />
                  <span className="text-[10px] uppercase tracking-widest text-gray-300 font-black">All Inclusive Pricing</span>
                </div>
              </div>
            </div>
          </motion.div> */}

          {/* Local Transfer Rates */}
          {/* <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-main/10 rounded-xl flex items-center justify-center text-orange-main">
                <MapPin size={20} />
              </div>
              <h2 className="text-lg font-black uppercase tracking-tight text-gray-900">Local Transfers</h2>
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">
              <table className="w-full border-collapse table-fixed">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border border-gray-300 px-3 py-4 text-[9px] lg:text-[11px] uppercase tracking-wider font-black text-left text-gray-600 w-[40%]">Service Type</th>
                    <th className="border border-gray-300 px-3 py-4 text-[9px] lg:text-[11px] uppercase tracking-wider font-black text-center text-gray-600">Etios</th>
                    <th className="border border-gray-300 px-3 py-4 text-[9px] lg:text-[11px] uppercase tracking-wider font-black text-right text-gray-600">Innova</th>
                  </tr>
                </thead>
                <tbody>
                  {localRates.map((item, i) => (
                    <tr key={i} className="hover:bg-orange-50/30 transition-colors">
                      <td className="border border-gray-300 px-3 py-4 text-[10px] lg:text-[13px] font-bold text-gray-800 break-words">{t(item.type)}</td>
                      <td className="border border-gray-300 px-3 py-4 text-[9px] lg:text-[12px] font-medium text-gray-500 text-center">₹ {item.etios}</td>
                      <td className="border border-gray-300 px-3 py-4 text-[10px] lg:text-[13px] font-bold text-orange-main text-right">₹ {item.innova}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div> */}

          {/* Bank Details & Payment Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-orange-main/10 rounded-xl flex items-center justify-center text-orange-main">
                <BadgeIndianRupee size={20} />
              </div>
              <h2 className="text-lg font-black uppercase tracking-tight text-gray-900">Payment Details</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Bank Details Card */}
              <div className="rounded-xl border border-gray-300 bg-white shadow-sm overflow-hidden">
                <div className="bg-gray-900 px-5 py-4">
                  <h3 className="text-orange-main font-black text-sm uppercase tracking-wider">Bank Details</h3>
                </div>
                <div className="p-5 space-y-3 text-[11px] lg:text-[13px]">
                  <div className="flex justify-between items-start border-b border-gray-100 pb-2">
                    <span className="font-bold text-gray-500 uppercase tracking-wide w-[35%]">Bank Name</span>
                    <span className="font-bold text-gray-800 text-right w-[60%]">HDFC Bank</span>
                  </div>
                  <div className="flex justify-between items-start border-b border-gray-100 pb-2">
                    <span className="font-bold text-gray-500 uppercase tracking-wide w-[35%]">Account Name</span>
                    <span className="font-bold text-gray-800 text-right w-[60%]">Khajuraho Routes</span>
                  </div>
                  <div className="flex justify-between items-start border-b border-gray-100 pb-2">
                    <span className="font-bold text-gray-500 uppercase tracking-wide w-[35%]">Account Number</span>
                    <span className="font-bold text-gray-800 text-right w-[60%] select-all">50200037879910</span>
                  </div>
                  <div className="flex justify-between items-start border-b border-gray-100 pb-2">
                    <span className="font-bold text-gray-500 uppercase tracking-wide w-[35%]">IFSC Code</span>
                    <span className="font-bold text-gray-800 text-right w-[60%] font-mono">HDFC0006647</span>
                  </div>
                  <div className="flex justify-between items-start border-b border-gray-100 pb-2">
                    <span className="font-bold text-gray-500 uppercase tracking-wide w-[35%]">Swift Code</span>
                    <span className="font-bold text-gray-800 text-right w-[60%] font-mono">HDFCINBBIDR</span>
                  </div>
                  <div className="flex justify-between items-start border-b border-gray-100 pb-2">
                    <span className="font-bold text-gray-500 uppercase tracking-wide w-[35%]">Branch Code</span>
                    <span className="font-bold text-gray-800 text-right w-[60%]">6647</span>
                  </div>
                  <div className="flex justify-between items-start border-b border-gray-100 pb-2">
                    <span className="font-bold text-gray-500 uppercase tracking-wide w-[35%]">Branch</span>
                    <span className="font-bold text-gray-800 text-right w-[60%]">Khajuraho</span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className="font-bold text-gray-500 uppercase tracking-wide w-[35%]">Address</span>
                    <span className="font-bold text-gray-800 text-right w-[60%] leading-relaxed">HDFC Bank Ltd., Jain Mandir Road, Behind Gautam Hotel Resort, Khajuraho, Chhatarpur - 471606</span>
                  </div>
                </div>
              </div>

              {/* UPI & QR Card */}
              <div className="rounded-xl border border-gray-300 bg-white shadow-sm overflow-hidden">
                <div className="bg-gray-900 px-5 py-4">
                  <h3 className="text-orange-main font-black text-sm uppercase tracking-wider">UPI / QR Payment</h3>
                </div>
                <div className="px-6 lg:px-8 flex flex-col items-center">
                  <div className="w-64 h-64 lg:w-100 lg:h-100 overflow-hidden flex items-center justify-center">
                    <img
                      src={qrImg}
                      alt="UPI QR Code"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="w-full space-y-2 text-center">
                    <div className=" px-2 py-2">
                      <span className="block text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-1">Google Pay / PhonePe</span>
                      <span className="text-xl lg:text-2xl font-black text-gray-900 tracking-wide select-all">9993910094</span>
                    </div>
                  </div>
                  <p className="text-[10px] lg:text-[11px] text-gray-400 font-medium text-center">
                    Scan QR code or use UPI number to make payment
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

        </div>
      </section>
    </main>
  );
};

export default TransportCharges;
